/**
 * Sprint 233 — Package Detail + Checkout / Quote flow.
 *
 * Two modes:
 *  • Browse & Pick — user goes through Influencer Discovery to choose creators
 *    (parked here via sessionStorage from /client/influencers?package=<slug>)
 *  • Auto-Match — user picks a category + briefing, we'll match the creator
 *
 * Premium Custom — flips checkout into a Quote Request (Stripe disabled).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Check, Loader2, Sparkles, Users, MessagesSquare,
  ShieldCheck, Clock, RefreshCw, ExternalLink, CreditCard, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const BRAND = '#0DEAE3';

const PackageDetail = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [meta, setMeta] = useState({ categories: [] });
  const [mode, setMode] = useState('browse_pick');
  const [category, setCategory] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [requirements, setRequirements] = useState({ go_live: '', target_audience: '', notes: '' });
  const [selectedCreators, setSelectedCreators] = useState([]);
  const [busy, setBusy] = useState(false);

  // Restore creators chosen via "Browse Creators" hand-off
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`pkg-select-${slug}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setSelectedCreators(parsed);
          setMode('browse_pick');
        }
      }
    } catch { /* ignore */ }
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get(`/packages/${slug}`),
          api.get('/influencer/meta'),
        ]);
        if (!cancelled) {
          setPkg(p.data);
          setMeta(m.data || { categories: [] });
        }
      } catch {
        if (!cancelled) toast.error('Package not found');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const startCheckout = async () => {
    if (!pkg) return;
    if (pkg.is_custom_quote) return submitQuote();
    if (mode === 'browse_pick' && selectedCreators.length === 0) {
      toast.error('Pick at least one creator or switch to Auto-Match');
      return;
    }
    if (mode === 'auto_match' && !category) {
      toast.error('Pick a category for Auto-Match');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/packages/${slug}/checkout`, {
        origin_url: window.location.origin,
        mode,
        category: category || null,
        selected_creator_ids: selectedCreators.map(c => c.user_id || c.id),
        requirements,
        client_notes: clientNotes,
      });
      sessionStorage.removeItem(`pkg-select-${slug}`);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  const submitQuote = async () => {
    if (!pkg) return;
    setBusy(true);
    try {
      await api.post(`/packages/${slug}/quote-request`, {
        mode, category: category || null, requirements, client_notes: clientNotes,
      });
      toast.success('Quote request sent — our team will reach out within 1 business day.');
      navigate('/client/packages/orders');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit quote');
    } finally {
      setBusy(false);
    }
  };

  const browseCreators = () => {
    sessionStorage.setItem(`pkg-context-${slug}`, JSON.stringify({
      slug, name: pkg?.name, tier: pkg?.estimated_tier, category,
    }));
    navigate(`/client/influencers?package=${slug}${category ? `&category=${encodeURIComponent(category)}` : ''}${pkg?.estimated_tier ? `&tier=${pkg.estimated_tier}` : ''}`);
  };

  if (!pkg) {
    return (
      <div className="min-h-screen bg-[#0B0F14] grid place-items-center text-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#0DEAE3]" />
      </div>
    );
  }

  const isCustom = pkg.is_custom_quote;

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white" data-testid="package-detail">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0B0F14]/95 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/packages')}
                  className="text-white/70 hover:text-white hover:bg-white/5" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Sparkles className="w-4 h-4 text-[#0DEAE3]" />
          <h1 className="text-base font-semibold tracking-tight">{pkg.name}</h1>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — Package spec */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
            <p className="text-[11px] uppercase tracking-widest text-[#0DEAE3]">{isCustom ? 'Premium · Custom' : 'Fixed-price package'}</p>
            <h2 className="text-3xl font-bold tracking-tight mt-1">{pkg.name}</h2>
            <p className="text-white/60 mt-1">{pkg.tagline}</p>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <MetaBlock icon={Clock}     label="Turnaround" value={pkg.turnaround_days ? `${pkg.turnaround_days} days` : 'Custom'} />
              <MetaBlock icon={RefreshCw} label="Revisions"  value={`${pkg.revisions}x`} />
              <MetaBlock icon={Users}     label="Tier"       value={(pkg.estimated_tier || '—').toUpperCase()} />
            </div>

            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Deliverables</p>
              <ul className="space-y-1.5">
                {(pkg.deliverables || []).map(d => (
                  <li key={d} className="flex items-start gap-2 text-sm text-white/85">
                    <Check className="w-4 h-4 text-[#0DEAE3] mt-0.5" /> {d}
                  </li>
                ))}
              </ul>
            </div>

            {pkg.usage_rights && (
              <div className="mt-6">
                <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> Usage Rights
                </p>
                <p className="text-sm text-white/75">{pkg.usage_rights}</p>
              </div>
            )}

            {(pkg.optional_addons || []).length > 0 && (
              <div className="mt-6">
                <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Optional Add-Ons</p>
                <div className="space-y-1.5">
                  {pkg.optional_addons.map(a => (
                    <div key={a.name} className="flex items-center justify-between text-sm text-white/80 bg-white/[0.03] border border-white/5 rounded-md px-3 py-2">
                      <span>{a.name}</span>
                      <span className="text-white/50">
                        {a.price_cents == null ? '+ Quote' : `+ $${(a.price_cents / 100).toFixed(0)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Brief / requirements */}
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-[#0DEAE3]" /> Brief us</h3>
            <p className="text-xs text-white/50">A few details help our team kick off faster.</p>
            <div>
              <Label className="text-xs text-white/60">Target audience (optional)</Label>
              <Input value={requirements.target_audience}
                     onChange={(e) => setRequirements({ ...requirements, target_audience: e.target.value })}
                     placeholder="e.g. women 25-34 in Atlanta interested in wellness"
                     className="bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1"
                     data-testid="req-target-audience" />
            </div>
            <div>
              <Label className="text-xs text-white/60">When do you need this live?</Label>
              <Input value={requirements.go_live}
                     onChange={(e) => setRequirements({ ...requirements, go_live: e.target.value })}
                     placeholder="e.g. By Aug 15 or ASAP"
                     className="bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1"
                     data-testid="req-go-live" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Additional notes for the creator</Label>
              <Textarea value={clientNotes}
                        onChange={(e) => setClientNotes(e.target.value)}
                        placeholder="Brand voice, must-mention products, content do's & don'ts…"
                        rows={4}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1"
                        data-testid="req-client-notes" />
            </div>
          </div>
        </div>

        {/* RIGHT — Mode + Checkout */}
        <div className="space-y-4 lg:sticky lg:top-20 self-start">
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-5">
            <p className="text-[11px] uppercase tracking-widest text-white/40">Price</p>
            <p className="text-3xl font-bold mt-1">
              {isCustom ? <span className="text-[#0DEAE3]">Custom Quote</span> : pkg.price_display}
              {pkg.starts_at_only && !isCustom && <span className="text-xs text-white/40 ml-1">+ add-ons</span>}
            </p>

            {!isCustom && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <ModeButton testId="mode-browse_pick" active={mode === 'browse_pick'} onClick={() => setMode('browse_pick')}
                            title="Browse & Pick" subtitle="Choose creators yourself" />
                <ModeButton testId="mode-auto_match"  active={mode === 'auto_match'}  onClick={() => setMode('auto_match')}
                            title="Auto-Match" subtitle="We pick for you" />
              </div>
            )}

            <div className="mt-4">
              <Label className="text-xs text-white/60">Creator category</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm focus:border-[#0DEAE3]/50 focus:outline-none"
                      data-testid="category-select">
                <option value="" className="bg-[#0B0F14]">Any category</option>
                {(meta.categories || []).map(c => (
                  <option key={c} value={c} className="bg-[#0B0F14]">{c}</option>
                ))}
              </select>
            </div>

            {/* Selected creators (browse_pick) */}
            {!isCustom && mode === 'browse_pick' && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-white/60">Selected creators</Label>
                  <button type="button" onClick={browseCreators}
                          className="text-[11px] text-[#0DEAE3] hover:underline flex items-center gap-1"
                          data-testid="browse-creators-btn">
                    Browse <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
                {selectedCreators.length === 0 ? (
                  <p className="text-[11px] text-white/40 bg-white/[0.03] border border-dashed border-white/10 rounded-md px-3 py-3">
                    No creators selected yet. Click <em>Browse</em> to pick from the Creator Network.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {selectedCreators.map(c => (
                      <div key={c.user_id || c.id} className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-md px-2 py-1.5">
                        {c.photo && <img src={c.photo} alt="" className="w-7 h-7 rounded-full object-cover" />}
                        <span className="text-xs text-white/90 truncate flex-1">{c.name}</span>
                        <button type="button"
                                onClick={() => {
                                  const next = selectedCreators.filter(x => (x.user_id || x.id) !== (c.user_id || c.id));
                                  setSelectedCreators(next);
                                  sessionStorage.setItem(`pkg-select-${slug}`, JSON.stringify(next));
                                }}
                                className="text-[10px] text-rose-400 hover:underline">remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button onClick={startCheckout} disabled={busy}
                    className="w-full mt-5 bg-[#0DEAE3] hover:bg-[#0DEAE3]/90 text-[#0B0F14] font-semibold"
                    data-testid="checkout-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> :
                isCustom ? <MessagesSquare className="w-4 h-4 mr-1.5" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
              {isCustom ? 'Request Quote' : `Continue to Payment · ${pkg.price_display}`}
              {!busy && <ArrowRight className="w-4 h-4 ml-1.5" />}
            </Button>

            <p className="mt-2 text-[10px] text-white/40 text-center">
              {isCustom ? 'A team member will reply with a tailored proposal within 1 business day.'
                        : 'Secure Stripe Checkout. Refundable if creators cannot be matched within 7 days.'}
            </p>
          </div>

          <button type="button" onClick={() => navigate('/client/packages/orders')}
                  className="w-full text-xs text-white/50 hover:text-white py-2"
                  data-testid="my-orders-btn">
            View my package orders →
          </button>
        </div>
      </div>
    </div>
  );
};

const MetaBlock = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
    <p className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1">
      <Icon className="w-3 h-3 text-[#0DEAE3]" /> {label}
    </p>
    <p className="text-white font-bold text-base mt-1">{value}</p>
  </div>
);

const ModeButton = ({ active, onClick, title, subtitle, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className={`text-left rounded-lg border p-2.5 transition-all ${
            active ? 'bg-[#0DEAE3] border-[#0DEAE3] text-[#0B0F14] shadow-[0_0_18px_rgba(13,234,227,0.35)]'
                   : 'bg-white/[0.03] border-white/10 text-white hover:border-[#0DEAE3]/50'}`}>
    <p className="text-sm font-bold">{title}</p>
    <p className={`text-[10px] ${active ? 'text-[#0B0F14]/70' : 'text-white/45'}`}>{subtitle}</p>
  </button>
);

export default PackageDetail;
